/*
 This file is part of GNU Taler
 (C) 2026 Taler Systems S.A.

 GNU Taler is free software; you can redistribute it and/or modify it under the
 terms of the GNU General Public License as published by the Free Software
 Foundation; either version 3, or (at your option) any later version.

 GNU Taler is distributed in the hope that it will be useful, but WITHOUT ANY
 WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 A PARTICULAR PURPOSE.  See the GNU General Public License for more details.

 You should have received a copy of the GNU General Public License along with
 GNU Taler; see the file COPYING.  If not, see <http://www.gnu.org/licenses/>
 */

import type { VNode } from "preact";
import { useCallback, useEffect, useRef } from "preact/hooks";
import {
  AccessToken,
  HttpStatusCode,
  TalerMerchantApi,
} from "@gnu-taler/taler-util";
import { useLocation } from "wouter-preact";
import { exchangeClient, merchantClient } from "../api/client.js";
import { currentAccount, currentBackendUrl } from "../stores/session.js";
import { useTranslation } from "../context/translation.js";
import {
  KycAuthInstructionsScreen,
  type KycAuthInstructionsScreenProps,
} from "../screens/KycAuthInstructionsScreen.js";
import {
  configurationFailure,
  normalizeApiFailure,
  unwrapEmpty,
} from "../api/failure.js";
import { useInstanceKycStatus } from "../api/hooks.js";
import type { ApiFailure } from "../api/failure.js";

export type ExchangeTermsReadResult =
  | { type: "ok"; version: string }
  | { type: "not-required" }
  | { type: "missing-version" }
  | { type: "fail"; error: ApiFailure };

/** Read the current exchange terms version without inventing a fallback. */
export async function readExchangeTerms(
  exchangeUrl: string,
): Promise<ExchangeTermsReadResult> {
  try {
    const result = await exchangeClient(exchangeUrl).getTermsMeta();
    if (result.type === "ok") {
      const version = result.body.etag?.trim();
      return version ? { type: "ok", version } : { type: "missing-version" };
    }
    if (result.case === HttpStatusCode.NotImplemented) {
      return { type: "not-required" };
    }
    return { type: "fail", error: normalizeApiFailure(result) };
  } catch (cause) {
    return { type: "fail", error: normalizeApiFailure(cause) };
  }
}

/** Submit only the version captured from the exchange's /terms response. */
export async function acceptExchangeTermsEarly(args: {
  token: string;
  exchangeUrl: string;
  termsVersion: string;
  merchantRootUrl: URL;
  account: string;
}): Promise<void> {
  const client = merchantClient({
    rootUrl: args.merchantRootUrl,
    account: args.account,
  });
  const result = await client.acceptTosEarly(args.token as AccessToken, {
    exchange_url: args.exchangeUrl,
    tos_version: args.termsVersion,
  });
  unwrapEmpty(result);
}

/**
 * The backend has answered this shape under several names over time, and a
 * bare payto string is also legal, so the list is dug out rather than assumed.
 */
function extractInstructions(
  body: unknown,
): TalerMerchantApi.WireTransferInstructionDetail[] {
  const b = body as Record<string, unknown> | undefined;
  const candidates = [
    b?.wire_instructions,
    b?.wire_transfer_instructions,
    b?.payto_kycauths,
    b?.payto_uris,
  ];
  for (const c of candidates) {
    if (Array.isArray(c)) return c;
  }
  if (b?.target_payto)
    return [b as unknown as TalerMerchantApi.WireTransferInstructionDetail];
  if (typeof body === "string") return [body as never];
  return [];
}

export function normalizeKycExchangeUrl(
  value: string | undefined,
): string | undefined {
  if (!value) return undefined;
  try {
    const url = new URL(value);
    url.hash = "";
    url.search = "";
    return url.href.endsWith("/") ? url.href : `${url.href}/`;
  } catch {
    return undefined;
  }
}

export function matchesKycInstructionTarget(
  item: Pick<
    TalerMerchantApi.MerchantAccountKycRedirect,
    "h_wire" | "exchange_url"
  >,
  hWire: string | undefined,
  exchangeUrl: string | undefined,
): boolean {
  return Boolean(
    hWire &&
    item.h_wire === hWire &&
    normalizeKycExchangeUrl(item.exchange_url) ===
      normalizeKycExchangeUrl(exchangeUrl),
  );
}

export function kycInstructionTargetKey(
  hWire: string | undefined,
  exchangeUrl: string | undefined,
): string | undefined {
  const normalizedExchangeUrl = normalizeKycExchangeUrl(exchangeUrl);
  return hWire && normalizedExchangeUrl
    ? `${hWire}\n${normalizedExchangeUrl}`
    : undefined;
}

export function KycAuthInstructionsRoute(
  props: KycAuthInstructionsScreenProps,
): VNode {
  const { t } = useTranslation();
  const { token, hWire, exchangeUrl } = props;
  const [, setLocation] = useLocation();
  const { kycData, mutate: refreshKycStatus } = useInstanceKycStatus(token);
  const sawWireRequiredFor = useRef<string | undefined>(undefined);

  useEffect(() => {
    const targetKey = kycInstructionTargetKey(hWire, exchangeUrl);
    const watched = kycData.find((item) =>
      matchesKycInstructionTarget(item, hWire, exchangeUrl),
    );
    if (!watched || !targetKey) return;
    if (
      watched.status ===
      TalerMerchantApi.MerchantAccountKycStatus.KYC_WIRE_REQUIRED
    ) {
      sawWireRequiredFor.current = targetKey;
      return;
    }
    if (sawWireRequiredFor.current === targetKey) {
      setLocation("/money/payout-accounts?kyc_updated=1");
    }
  }, [kycData, hWire, exchangeUrl, setLocation]);

  // Stable across renders, or the screen's effect would refetch on every one.
  const onLoadInstructions = useCallback(async () => {
    if (!token || !hWire || !exchangeUrl) {
      return {
        type: "fail" as const,
        error: configurationFailure(
          t`This account cannot be verified yet: some details are missing.`,
        ),
      };
    }
    let rootUrl: URL;
    try {
      rootUrl = new URL(currentBackendUrl.value);
    } catch (cause) {
      return {
        type: "fail" as const,
        error: configurationFailure(
          t`Invalid merchant backend configuration.`,
          cause,
        ),
      };
    }
    const account = currentAccount.value;
    if (!account) {
      return {
        type: "fail" as const,
        error: configurationFailure(t`Merchant account context is missing.`),
      };
    }
    try {
      const client = merchantClient({ rootUrl, account });
      const res = await client.getInstancePrivateAccountKycauth(
        token as AccessToken,
        {
          wireAccountHash: hWire,
          exchangeBaseUrl: exchangeUrl,
        },
      );
      if (res.type !== "ok") {
        return { type: "fail" as const, error: normalizeApiFailure(res) };
      }
      return {
        type: "ok" as const,
        instructions: extractInstructions(res.body),
      };
    } catch (cause) {
      return { type: "fail" as const, error: normalizeApiFailure(cause) };
    }
  }, [token, hWire, exchangeUrl, t]);

  const onLoadTerms = useCallback(async () => {
    if (!exchangeUrl) {
      return {
        type: "fail" as const,
        error: configurationFailure(
          t`Missing details, so the terms cannot be recorded.`,
        ),
      };
    }
    const result = await readExchangeTerms(exchangeUrl);
    if (result.type === "missing-version") {
      return {
        type: "fail" as const,
        error: configurationFailure(
          t`The payment service did not identify the terms version.`,
        ),
      };
    }
    if (result.type !== "not-required") return result;
    try {
      await refreshKycStatus();
      return result;
    } catch (cause) {
      return {
        type: "fail" as const,
        error: normalizeApiFailure(cause),
      };
    }
  }, [exchangeUrl, refreshKycStatus, t]);

  const onAcceptTos = useCallback(
    async (termsVersion: string) => {
      if (!token || !exchangeUrl)
        throw configurationFailure(
          t`Missing details, so the terms cannot be recorded.`,
        );
      const account = currentAccount.value;
      if (!account)
        throw configurationFailure(t`Merchant account context is missing.`);
      let rootUrl: URL;
      try {
        rootUrl = new URL(currentBackendUrl.value);
      } catch (cause) {
        throw configurationFailure(t`Invalid backend configuration.`, cause);
      }
      try {
        await acceptExchangeTermsEarly({
          token,
          exchangeUrl,
          termsVersion,
          merchantRootUrl: rootUrl,
          account,
        });
        await refreshKycStatus();
      } catch (cause) {
        throw normalizeApiFailure(cause, { outcome: "indeterminate" });
      }
    },
    [token, exchangeUrl, refreshKycStatus, t],
  );

  return (
    <KycAuthInstructionsScreen
      {...props}
      onLoadInstructions={onLoadInstructions}
      onLoadTerms={onLoadTerms}
      onAcceptTos={onAcceptTos}
    />
  );
}
